import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Plus, Settings, Trash2, Star, MoreVertical, Columns } from './icons/HeroshhiIcons';

// ============================================
// COLUMN LAYOUTS ("Columns" button on the Leads tab)
// Built-in layouts (view.builtin) come first and can't be edited or deleted;
// saved layouts follow, with ★ default and edit / set default / delete.
// ============================================
export default function ViewSelector({
  views = [],
  activeViewId,
  onSelectView,
  onCreateNew,
  onEditView,
  onDeleteView,
  onSetDefault,
  loading = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const dropdownRef = useRef(null);
  const saved = views.filter(v => !v.builtin);
  const activeView = views.find(v => v._id === activeViewId) || views.find(v => v.isDefault) || views[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) { setIsOpen(false); setMenuOpenFor(null); }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMenuAction = (action, view, e) => {
    e.stopPropagation();
    setMenuOpenFor(null);
    if (action === 'edit') { onEditView(view); setIsOpen(false); }
    if (action === 'setDefault') onSetDefault(view._id);
    if (action === 'delete') {
      if (view.isDefault) { alert('Cannot delete the default layout.'); return; }
      if (window.confirm(`Delete the "${view.name}" layout?`)) onDeleteView(view._id);
    }
  };

  const row = (view) => {
    const on = activeView?._id === view._id;
    return (
      <div key={view._id} className={`relative flex items-center ${on ? 'bg-slate-100' : 'hover:bg-gray-50'}`}>
        <button type="button" onClick={() => { onSelectView(view._id); setIsOpen(false); }}
          className="flex-1 min-w-0 flex items-center gap-3 px-4 min-h-[48px] text-left">
          <span className="w-5 flex-none flex items-center justify-center">{on ? <Check size={16} className="text-[#10203A]" /> : null}</span>
          <span className="min-w-0">
            <span className="flex items-center gap-1.5">
              <span className={`truncate ${on ? 'font-extrabold text-[#10203A]' : 'font-semibold text-gray-800'}`}>{view.name}</span>
              {view.isDefault && !view.builtin && <Star size={12} className="text-amber-600 fill-amber-500" />}
            </span>
            <span className="block text-xs text-gray-600">{view.builtin ? view.hint : (() => { const n = view.columns?.filter(c => c.visible).length || 0; return `${n} ${n === 1 ? 'column' : 'columns'}`; })()}</span>
          </span>
        </button>
        {!view.builtin && (
          <div className="relative pr-2">
            <button type="button" aria-label={`Options for ${view.name}`}
              onClick={(e) => { e.stopPropagation(); setMenuOpenFor(menuOpenFor === view._id ? null : view._id); }}
              className="min-h-[36px] min-w-[36px] flex items-center justify-center hover:bg-gray-200 rounded">
              <MoreVertical size={16} className="text-gray-500" />
            </button>
            {menuOpenFor === view._id && (
              <div className="absolute right-0 top-full mt-1 w-44 bg-white border rounded-lg shadow-lg z-10 py-1">
                <button type="button" onClick={(e) => handleMenuAction('edit', view, e)} className="w-full px-3 min-h-[38px] text-left text-sm hover:bg-gray-50 flex items-center gap-2"><Settings size={14} /> Edit layout</button>
                {!view.isDefault && <button type="button" onClick={(e) => handleMenuAction('setDefault', view, e)} className="w-full px-3 min-h-[38px] text-left text-sm hover:bg-gray-50 flex items-center gap-2"><Star size={14} /> Set as default</button>}
                {!view.isDefault && saved.length > 1 && <button type="button" onClick={(e) => handleMenuAction('delete', view, e)} className="w-full px-3 min-h-[38px] text-left text-sm hover:bg-red-50 text-red-700 flex items-center gap-2"><Trash2 size={14} /> Delete</button>}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button type="button" onClick={() => setIsOpen(!isOpen)} disabled={loading} aria-expanded={isOpen}
        className={`min-h-[40px] flex items-center gap-2 px-3 border rounded-xl text-sm font-bold text-[#10203A] ${isOpen ? 'border-[#10203A] bg-slate-50' : 'border-gray-300 bg-white hover:border-gray-400'}`}>
        <Columns size={16} />
        <span className="max-w-[160px] truncate">Columns: {activeView?.name || 'Default'}</span>
        <ChevronDown size={16} className={`text-gray-500 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 sm:left-0 sm:right-auto mt-2 w-80 max-w-[calc(100vw-24px)] bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
          <div className="max-h-80 overflow-y-auto">
            {views.some(v => v.builtin) && <p className="px-4 pt-3 pb-1 text-xs font-bold text-gray-600">Suggested</p>}
            {views.filter(v => v.builtin).map(row)}
            <p className="px-4 pt-3 pb-1 text-xs font-bold text-gray-600 border-t border-gray-100 mt-1">Your layouts ({saved.length}/10)</p>
            {saved.map(row)}
          </div>
          <div className="border-t">
            <button type="button" onClick={() => { setIsOpen(false); onCreateNew(); }} disabled={saved.length >= 10}
              className="w-full px-4 min-h-[48px] text-left text-sm font-bold text-[#10203A] hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2">
              <Plus size={18} /> Create new layout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}